import { Field, Form, Formik, useFormikContext } from "formik";
import React, { useEffect, useState, useCallback, useRef } from "react";
import OrderSummary from "../orderSummary/OrderSummary";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import api from "../../api";
import Swal from "sweetalert2";
import { clearCart } from "../../store/reducers/cartSlice";
import { getCurrentCustomer } from "../../utils/auth";
import { useTranslation } from "react-i18next";

// ── Validation Schema ──────────────────────────────────────────────────────────
const SignupSchema = Yup.object().shape({
  customer_name:    Yup.string().required("Name is required!"),
  customer_email:   Yup.string().email("Invalid email"),
  customer_phone:   Yup.string().required("Phone number is required!"),
  shipping_address: Yup.string().required("Address is required!"),
  division:         Yup.string().required("Division is required!"),
  district:         Yup.string().required("District is required!"),
  thana:            Yup.string(),
  payment_method:   Yup.string().required("Payment Method is required"),
});

// ── Cascading Area Picker with Live Charge Calculation ────────────────────────
function AreaPicker({ shippingCost, onShippingChange }) {
  const { values, setFieldValue, errors, touched } = useFormikContext();
  const [divisions, setDivisions]   = useState([]);
  const [districts, setDistricts]   = useState([]);
  const [thanas, setThanas]         = useState([]);
  const [loadingCharge, setLoadingCharge] = useState(false);
  const [chargeInfo, setChargeInfo] = useState(null);

  // Load divisions on mount
  useEffect(() => {
    api.get("/delivery-zones/divisions")
      .then((res) => setDivisions(res?.data?.data || []))
      .catch(() => {
        // Fallback to static list if API fails
        setDivisions(["Dhaka", "Chattogram", "Rajshahi", "Khulna", "Sylhet", "Barishal", "Rangpur", "Mymensingh"]);
      });
  }, []);

  // Load districts when division changes
  useEffect(() => {
    if (!values.division) {
      setDistricts([]);
      setThanas([]);
      setFieldValue("district", "");
      setFieldValue("thana", "");
      setChargeInfo(null);
      onShippingChange(0);
      return;
    }
    api.get(`/delivery-zones/districts?division=${values.division}`)
      .then((res) => setDistricts(res?.data?.data || []))
      .catch(() => setDistricts([]));

    // Calculate charge at division level immediately
    calculateCharge(values.division, null, null);
    setFieldValue("district", "");
    setFieldValue("thana", "");
    setThanas([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.division]);

  // Load thanas when district changes
  useEffect(() => {
    if (!values.district || !values.division) {
      setThanas([]);
      setFieldValue("thana", "");
      return;
    }
    api.get(`/delivery-zones/thanas?division=${values.division}&district=${values.district}`)
      .then((res) => setThanas(res?.data?.data || []))
      .catch(() => setThanas([]));

    // Recalculate charge at district level
    calculateCharge(values.division, values.district, null);
    setFieldValue("thana", "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.district]);

  // Recalculate when thana changes
  useEffect(() => {
    if (!values.thana || !values.district || !values.division) return;
    calculateCharge(values.division, values.district, values.thana);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.thana]);

  const calculateCharge = async (division, district, thana) => {
    if (!division) return;
    setLoadingCharge(true);
    try {
      const params = new URLSearchParams({ division });
      if (district) params.append("district", district);
      if (thana)    params.append("thana", thana);

      const res = await api.get(`/delivery-zones/calculate?${params.toString()}`);
      const data = res?.data?.data;
      if (data) {
        setChargeInfo(data);
        onShippingChange(data.delivery_charge);
      }
    } catch {
      // Silently fail — keep last known charge
    } finally {
      setLoadingCharge(false);
    }
  };

  return (
    <div className="col-span-6 grid grid-cols-6 gap-4">
      {/* Division */}
      <div className="col-span-6 sm:col-span-2">
        <label htmlFor="division" className="block text-gray-500 font-medium text-sm leading-none mb-2">
          Division <span className="text-red-400">*</span>
        </label>
        <div className="relative">
          <Field
            as="select"
            id="division"
            name="division"
            className="py-2 px-4 w-full appearance-none border text-sm text-gray-700 rounded-md min-h-12 transition duration-200 bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12 pr-10"
          >
            <option value="">Select Division</option>
            {divisions.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </Field>
          <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        {errors.division && touched.division && (
          <span className="text-red-400 text-xs mt-1 block">{errors.division}</span>
        )}
      </div>

      {/* District */}
      <div className="col-span-6 sm:col-span-2">
        <label htmlFor="district" className="block text-gray-500 font-medium text-sm leading-none mb-2">
          District <span className="text-red-400">*</span>
        </label>
        <div className="relative">
          <Field
            as="select"
            id="district"
            name="district"
            disabled={!values.division || districts.length === 0}
            className="py-2 px-4 w-full appearance-none border text-sm text-gray-700 rounded-md min-h-12 transition duration-200 bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12 pr-10 disabled:bg-gray-50 disabled:text-gray-400"
          >
            <option value="">{values.division ? "Select District" : "Select Division first"}</option>
            {districts.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </Field>
          <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        {errors.district && touched.district && (
          <span className="text-red-400 text-xs mt-1 block">{errors.district}</span>
        )}
      </div>

      {/* Thana */}
      <div className="col-span-6 sm:col-span-2">
        <label htmlFor="thana" className="block text-gray-500 font-medium text-sm leading-none mb-2">
          Thana / Upazila
        </label>
        <div className="relative">
          <Field
            as="select"
            id="thana"
            name="thana"
            disabled={!values.district || thanas.length === 0}
            className="py-2 px-4 w-full appearance-none border text-sm text-gray-700 rounded-md min-h-12 transition duration-200 bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12 pr-10 disabled:bg-gray-50 disabled:text-gray-400"
          >
            <option value="">{thanas.length > 0 ? "Select Thana (optional)" : "N/A"}</option>
            {thanas.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </Field>
          <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Delivery Charge Badge */}
      {values.division && (
        <div className="col-span-6">
          <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            loadingCharge
              ? "bg-gray-50 border border-gray-200 text-gray-400"
              : chargeInfo?.is_inside_dhaka
              ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
              : "bg-blue-50 border border-blue-200 text-blue-700"
          }`}>
            {loadingCharge ? (
              <>
                <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />
                <span>Calculating delivery charge...</span>
              </>
            ) : chargeInfo ? (
              <>
                <span className="text-lg">🚚</span>
                <span>
                  Delivery to <strong>{chargeInfo.is_inside_dhaka ? "Inside Dhaka" : values.district || values.division}</strong>:{" "}
                  <strong className="text-lg">৳{chargeInfo.delivery_charge}</strong>
                </span>
                {chargeInfo.estimated_days && (
                  <span className="ml-auto text-xs opacity-70">
                    Est. {chargeInfo.estimated_days} day{chargeInfo.estimated_days > 1 ? "s" : ""}
                  </span>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Checkout Component ────────────────────────────────────────────────────
function Checkout() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { cartItems } = useSelector((state) => state.cart);
  const customer = getCurrentCustomer();
  const { t } = useTranslation();

  const [couponCode, setCouponCode]       = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [shippingCost, setShippingCost]   = useState(0);

  const handleCouponChange = useCallback(({ couponCode: code, couponDiscount: disc }) => {
    setCouponCode(code);
    setCouponDiscount(disc);
  }, []);

  const onchangeSubmit = async (value) => {
    Swal.fire({
      title: "অর্ডার নিশ্চিত করবেন?",
      text: "একবার অর্ডার নিশ্চিত করলে তা বাতিল করা যাবে না!",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "হ্যাঁ, অর্ডার দিন",
      cancelButtonText: "না, বাতিল করুন",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const data = {
          items:            cartItems,
          customer_id:      customer?.id || undefined,
          shipping_method:  value.division,
          coupon_code:      couponCode || undefined,
          ...value,
        };

        try {
          const res = await api.post(`orders`, data);
          dispatch(clearCart());

          await Swal.fire({
            title: "🎉 ধন্যবাদ!",
            text:  "আপনার অর্ডারটি সফলভাবে সম্পন্ন হয়েছে। আমাদের টিম শীঘ্রই যোগাযোগ করবে।",
            icon:  "success",
            confirmButtonText:  "ঠিক আছে",
            background:         "#fffbea",
            color:              "#333",
            confirmButtonColor: "#3085d6",
          });

          const orderId = res?.data?.data?.id;
          navigate(orderId ? `/order/${orderId}` : "/");
        } catch (err) {
          console.error("Order error", err);
          const apiMessage =
            err?.response?.data?.message ||
            "Order could not be completed. Please try again.";
          Swal.fire({ title: "Error!", text: apiMessage, icon: "error", confirmButtonText: "OK" });
        }
      }
    });
  };

  return (
    <div className="bg-gray-50">
      <div className="mx-auto max-w-screen-2xl px-3 sm:px-10">
        <div className="py-10 lg:py-12 px-0 2xl:max-w-screen-2xl w-full xl:max-w-screen-xl flex flex-col md:flex-row lg:flex-row">
          {/* ── Left: Form ─────────────────────────────────────────── */}
          <div className="md:w-full lg:w-3/5 flex h-full flex-col order-2 sm:order-1 lg:order-1">
            <div className="mt-5 md:mt-0 md:col-span-2">
              <Formik
                initialValues={{
                  customer_name:    customer?.name || "",
                  customer_email:   "",
                  customer_phone:   "",
                  shipping_address: "",
                  division:         "",
                  district:         "",
                  thana:            "",
                  area:             "",
                  payment_method:   "cod",
                  customer_notes:   "",
                }}
                validationSchema={SignupSchema}
                onSubmit={async (values) => {
                  onchangeSubmit(values);
                }}
              >
                {({ errors, touched }) => (
                  <Form>
                    {/* Personal Details */}
                    <div>
                      <h2 className="font-semibold text-base text-gray-700 pb-3">
                        {t("checkout.personalDetails")}
                      </h2>
                      <div className="grid grid-cols-6 gap-6">
                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="customer_name" className="block text-gray-500 font-medium text-sm leading-none mb-2">
                            {t("checkout.fullName")} <span className="text-red-400">*</span>
                          </label>
                          <Field
                            className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12"
                            id="customer_name"
                            name="customer_name"
                            placeholder={t("checkout.enterName")}
                          />
                          {errors.customer_name && touched.customer_name && (
                            <span className="text-red-400 text-sm mt-2">{errors.customer_name}</span>
                          )}
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="customer_phone" className="block text-gray-500 font-medium text-sm leading-none mb-2">
                            {t("checkout.mobileNumber")} <span className="text-red-400">*</span>
                          </label>
                          <Field
                            className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12"
                            id="customer_phone"
                            name="customer_phone"
                            placeholder={t("checkout.enterPhone")}
                          />
                          {errors.customer_phone && touched.customer_phone && (
                            <span className="text-red-400 text-sm mt-2">{errors.customer_phone}</span>
                          )}
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="customer_email" className="block text-gray-500 font-medium text-sm leading-none mb-2">
                            {t("checkout.emailAddress")}
                          </label>
                          <Field
                            className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12"
                            id="customer_email"
                            name="customer_email"
                            placeholder={t("checkout.enterEmail")}
                          />
                          {errors.customer_email && touched.customer_email && (
                            <span className="text-red-400 text-sm mt-2">{errors.customer_email}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Shipping Details */}
                    <div className="mt-12">
                      <h2 className="font-semibold text-base text-gray-700 pb-3">
                        {t("checkout.shippingDetails")}
                      </h2>
                      <div className="grid grid-cols-6 gap-6 mb-8">
                        {/* Full address */}
                        <div className="col-span-6">
                          <label htmlFor="shipping_address" className="block text-gray-500 font-medium text-sm leading-none mb-2">
                            {t("checkout.fullAddress")} <span className="text-red-400">*</span>
                          </label>
                          <Field
                            className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12"
                            id="shipping_address"
                            name="shipping_address"
                            placeholder={t("checkout.enterAddress")}
                          />
                          {errors.shipping_address && touched.shipping_address && (
                            <span className="text-red-400 text-sm mt-2">{errors.shipping_address}</span>
                          )}
                        </div>

                        {/* Cascading Division → District → Thana with dynamic charge */}
                        <AreaPicker
                          shippingCost={shippingCost}
                          onShippingChange={setShippingCost}
                        />

                        {/* Payment Method */}
                        <div className="col-span-6 sm:col-span-3">
                          <label className="block text-gray-500 font-medium text-sm leading-none mb-2">
                            {t("checkout.paymentMethod")}
                          </label>
                          <div className="px-3 py-3 border border-gray-200 bg-white rounded-md">
                            <label className="cursor-pointer">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <span className="text-xl mr-3 text-gray-400">
                                    <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M47.5 104H432V51.52a16 16 0 00-19.14-15.69l-368 60.48a16 16 0 00-12 10.47A39.69 39.69 0 0147.5 104zm416 24h-416a16 16 0 00-16 16v288a16 16 0 0016 16h416a16 16 0 0016-16V144a16 16 0 00-16-16zM368 320a32 32 0 1132-32 32 32 0 01-32 32z" />
                                      <path d="M31.33 259.5V116c0-12.33 5.72-18.48 15.42-20 35.2-5.53 108.58-8.5 108.58-8.5s-8.33 16-27.33 16V128c18.5 0 31.33 23.5 31.33 23.5L84.83 236z" />
                                    </svg>
                                  </span>
                                  <h6 className="font-medium text-sm text-gray-600">Cash On Delivery</h6>
                                </div>
                                <Field
                                  type="radio"
                                  name="payment_method"
                                  value="cod"
                                  className="form-radio outline-none focus:ring-0 text-emerald-500"
                                />
                              </div>
                            </label>
                          </div>
                          {errors.payment_method && touched.payment_method && (
                            <span className="text-red-400 text-sm mt-2">{errors.payment_method}</span>
                          )}
                        </div>

                        {/* Notes */}
                        <div className="col-span-6">
                          <label htmlFor="customer_notes" className="block text-gray-500 font-medium text-sm leading-none mb-2">
                            {t("checkout.notes")}
                          </label>
                          <Field
                            className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12"
                            id="customer_notes"
                            name="customer_notes"
                            placeholder="Any special instructions for your order..."
                          />
                        </div>
                      </div>
                    </div>

                    {/* Buttons */}
                    {/* Buttons */}
                    <div className="flex flex-col sm:grid sm:grid-cols-6 gap-4 lg:gap-6 mt-10">
                      <div className="w-full sm:col-span-3 order-2 sm:order-1">
                        <Link
                          className="bg-indigo-50 border !no-underline border-indigo-100 rounded py-3 text-center text-sm font-medium !text-gray-700 hover:!text-gray-800 hover:border-gray-300 transition-all flex justify-center w-full"
                          to="/"
                        >
                          <span className="text-xl mr-2">
                            <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                              <path fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="32" d="M112 160l-64 64 64 64" />
                              <path fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="32" d="M64 224h294c58.76 0 106 49.33 106 108v20" />
                            </svg>
                          </span>
                          {t("checkout.continueShopping")}
                        </Link>
                      </div>
                      <div className="w-full sm:col-span-3 order-1 sm:order-2">
                        <button
                          type="submit"
                          className="bg-emerald-500 hover:bg-emerald-600 border border-emerald-500 transition-all rounded py-3 text-center text-sm font-medium text-white flex justify-center w-full"
                        >
                          {t("checkout.confirmOrder")}
                          <span className="text-xl ml-2">
                            <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                              <path fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="48" d="M268 112l144 144-144 144m124-144H100" />
                            </svg>
                          </span>
                        </button>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>

          {/* ── Right: Order Summary ────────────────────────────────── */}
          <OrderSummary
            onCouponChange={handleCouponChange}
            shippingCost={shippingCost}
          />
        </div>
      </div>
    </div>
  );
}

export default Checkout;
